import { useState, useCallback } from "react";
import { postKmsData, translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { StyledContentContainer } from "./styledComponents";
import { EditPlaylistTabs, MediaType } from "./types";
import { AddToPlaylistProps } from "./add-to-playlist/AddToPlaylist";
import { EditPlaylistHeader } from "./header/EditPlaylistHeader";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { KalturaPlaylist } from "kaltura-typescript-client/api/types/KalturaPlaylist";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { ButtonAnalyticsProvider, useButtonAnalytics, useFetchKmsData } from "@kaltura/mediaspace-shared-hooks";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { ContentListSkeleton } from "./skeleton/ContentListSkeleton";
import { Suspense } from "react";
import { PlaylistFormData, PlaylistInfoProps } from "./info/PlaylistInfo";
import { EditPlaylist, MediaListResponse } from "./EditPlaylist";
import { areMediaArraysEqual } from "./helpers";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { usePlaylistsListStore } from "@kaltura/mediaspace-shared-add-to-playlist";

export interface EditPlaylistContainerProps
    extends Omit<AddToPlaylistProps, "isEmptyPlaylistScreen" | "media" | "onMediaAdded">,
        Pick<PlaylistInfoProps, "deleteUrl" | "postDeleteRedirectUrl"> {
    playlist: KalturaPlaylist;
    updateApiUrl: string;
    getPlaylistMediaUrl: string;
    categoryId?: number | string;
    redirectUrl?: string;
    isWatchLater?: boolean;
    onBackCallback?: () => void;
}

/**
 * EditPlaylistContainer Component
 *
 * This component provides a UI for editing playlists. It allows users to view and edit (reorder/add/remove items) the content of a playlist
 * The component is placed in the shared by edit-playlist and edit-channel features
 */
export const EditPlaylistContainer = (props: EditPlaylistContainerProps) => {
    const {
        playlist,
        updateApiUrl,
        getPlaylistMediaUrl,
        deleteUrl,
        postDeleteRedirectUrl,
        categoryId,
        redirectUrl,
        isWatchLater = false,
        onBackCallback,
        ...addToPlaylistProps
    } = props;

    const [activeTab, setActiveTab] = useState<EditPlaylistTabs>(EditPlaylistTabs.CONTENT);
    const [processing, setProcessing] = useState(false);
    const [media, setMedia] = useState<MediaType[]>([]);
    const [originalMedia, setOriginalMedia] = useState<MediaType[]>([]);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const [playlistInfo, setPlaylistInfo] = useState<PlaylistFormData>({
        name: playlist.name ?? "",
        description: playlist.description ?? "",
    });
    const [formIsDirty, setFormIsDirty] = useState(false);

    // Show title on sm and above screens
    const theme = useTheme();
    const showTitle = !useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));

    // fetch data from KMS
    const fetchMediaData = useFetchKmsData<MediaListResponse>(
        {
            url: getPlaylistMediaUrl,
            data: { playlistid: playlist.id },
        },
        []
    );

    // Handler for updating the media list from ContentList (both removal and reordering)
    const handleMediaUpdate = useCallback(
        (updatedMedia: MediaType[]) => {
            // Set the media state
            setMedia(updatedMedia);

            // Only set original media on initial data load from API
            if (!initialDataLoaded) {
                setOriginalMedia([...updatedMedia]);
                setInitialDataLoaded(true);
            }
        },
        [initialDataLoaded]
    );

    // Handler for updating the playlist info from PlaylistInfo
    const handleInfoUpdate = (formData: PlaylistFormData, isDirty: boolean) => {
        setPlaylistInfo(formData);
        setFormIsDirty(isDirty);
    };

    const { showToast } = useToastsContext();

    const title = playlist.name || translate("Untitled Playlist");

    // Compute if media has changed by comparing current media with original media
    const isMediaChanged = initialDataLoaded && !areMediaArraysEqual(media, originalMedia);

    const sendButtonAnalytics = useButtonAnalytics();
    const { invalidate } = usePlaylistsListStore();

    // Handler for saving the playlist with updated media
    const handleUpdate = async () => {
        sendButtonAnalytics(
            "Save Playlist",
            ButtonClickAnalyticsType.SAVE,
            playlist.id,
            undefined,
            "Playlist edit - Header"
        );

        try {
            setProcessing(true);

            // Create an updated playlist object with the form data
            const updatedPlaylist = {
                ...playlist,
                name: playlistInfo.name,
                description: playlistInfo.description,
            };

            const result = await postKmsData(
                updateApiUrl,
                {
                    playlist: updatedPlaylist,
                    mediaIds: media.map((mediaObject) => mediaObject.entry.id),
                    categoryId: categoryId,
                }
            );
            if (result.success) {
                // If the playlist content or metadata changed, invalidate
                // the shared playlists cache so other consumers refetch.
                if (isMediaChanged || formIsDirty) {
                    invalidate();
                }
                showToast({
                    severity: "success",
                    message: translate(`Playlist "%1" was updated successfully`, [title]),
                });

                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }
                else {
                    onBackCallback?.();
                }
            }
            else {
                // result.errorMessage is already being translated in server side
                showToast({ severity: "error", message: result.errorMessage });
            }
            setProcessing(false);
        }
        catch (error) {
            showToast({ severity: "error", message: translate(`Error updating playlist "%1"`, [title]) });
            setProcessing(false);
        }
    };

    return (
        <ButtonAnalyticsProvider applicationFeature={"Playlists"}>
            <EditPlaylistHeader
                title={title}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onSave={handleUpdate}
                processing={processing}
                showTitle={showTitle}
                hasChanges={formIsDirty || isMediaChanged}
                playlistId={playlist.id}
                isWatchLater={isWatchLater}
                onBackCallback={onBackCallback}
            />
            <StyledContentContainer>
                <ErrorBoundary fallback={<ContentListSkeleton />}>
                    <Suspense fallback={<ContentListSkeleton />}>
                        <EditPlaylist
                            fetchMediaData={fetchMediaData}
                            tabs={addToPlaylistProps.tabs || []}
                            getMediaApiUrl={addToPlaylistProps.getMediaApiUrl}
                            deleteUrl={deleteUrl}
                            postDeleteRedirectUrl={postDeleteRedirectUrl}
                            onMediaUpdate={handleMediaUpdate}
                            onInfoUpdate={handleInfoUpdate}
                            onSave={handleUpdate}
                            showTitle={showTitle}
                            activeTab={activeTab}
                            playlist={playlist}
                            formValues={playlistInfo}
                            pageSize={addToPlaylistProps.pageSize}
                            isWatchLater={isWatchLater}
                        />
                    </Suspense>
                </ErrorBoundary>
            </StyledContentContainer>
        </ButtonAnalyticsProvider>
    );
};
